import React, { useEffect, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/joy";
import NumericKeypad, { Dots } from "components/security/NumericKeypad";
import { theme } from "styles/theme";
import { useRecoilValue, useSetRecoilState } from "recoil";
import {
  appBiometricEnabledAtom,
  appLockEnabledAtom,
  appLockPinHashAtom,
} from "recoil/appLockAtoms";
import { nativeBridge } from "utils/nativeBridge";
import { isValidPin } from "utils/pinLock";
import { useLocation } from "react-router-dom";
import { appLockApi } from "api/appLockApi";
import {
  useDisableAppLock,
  useUpdateBiometricEnabled,
} from "api/appLockQueries";
import FloatingArea from "components/common/FloatingArea";
import CustomTypography from "components/common/CustomTypography";
import TaskHeader from "components/task/header/TaskHeader";
import ContentTemplateV2 from "components/task/content/ContentTemplateV2";
import useIsInappWebview from "hooks/useIsInappWebview";
import { userAtom } from "recoil/userAtom";
import useGoBack from "hooks/useGoBack";
import { useChannelTalkContext } from "contexts/ChannelTalkContext";

export default function AppLockUnlockScreen() {
  const [pin, setPin] = React.useState("");
  const [, setError] = React.useState<string>("");
  const [helpText, setHelpText] =
    React.useState<string>("비밀번호를 입력해주세요.");
  const [requesting, setRequesting] = React.useState(false);
  const [bioSupported, setBioSupported] = React.useState<boolean>(false);
  const bioEnabled = useRecoilValue(appBiometricEnabledAtom);
  const setEnabled = useSetRecoilState(appLockEnabledAtom);
  const setPinHash = useSetRecoilState(appLockPinHashAtom);
  const setBioEnabled = useSetRecoilState(appBiometricEnabledAtom);
  const userState = useRecoilValue(userAtom);
  const [isBioRequesting, setIsBioRequesting] = useState(false);
  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const isInappWebview = useIsInappWebview();

  const disableMutation = useDisableAppLock();
  const updateBioMutation = useUpdateBiometricEnabled();
  const location = useLocation();
  const nextAction = new URLSearchParams(location.search).get("next") || "";

  const headerRef = useRef<HTMLDivElement | null>(null);
  const floatingRef = useRef<HTMLDivElement | null>(null);
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);
  const goBack = useGoBack();

  const { openChat } = useChannelTalkContext();

  // Measure available vertical space between header and keypad
  React.useEffect(() => {
    const measure = () => {
      const viewport =
        window.innerHeight || document.documentElement.clientHeight;
      const headerH = headerRef.current?.offsetHeight ?? 0;
      const floatingH = floatingRef.current?.offsetHeight ?? 0;
      const avail = Math.max(0, viewport - headerH - floatingH);
      setAvailableHeight(avail);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
    };
  }, []);

  const didCompleteRef = useRef(false);
  const onAuthSuccess = React.useCallback(async () => {
    if (didCompleteRef.current) return;
    didCompleteRef.current = true;
    if (nextAction === "disable") {
      setRequesting(true);
      try {
        const res = await disableMutation.mutateAsync();
        if (typeof res?.isEnabled === "boolean") {
          setEnabled(res.isEnabled);
        }
        // OFF 시 PIN 제거
        setPinHash(null);
        // UX: 앱 잠금을 끄면 생체 인증도 함께 끈다
        try {
          setBioEnabled(false);
          const data = await updateBioMutation.mutateAsync(false);
          if (typeof data?.isBiometricEnabled === "boolean") {
            setBioEnabled(!!data.isBiometricEnabled);
          }
        } catch {
          // 서버 저장 실패 시 콘솔만
          console.error(
            "생체 인증 설정 저장에 실패했습니다. 잠시 후 다시 시도하세요."
          );
        }
      } catch (e) {
        // 실패 시 재시도 가능하도록 상태 원복
        didCompleteRef.current = false;
        setRequesting(false);
        setHelpText("처리 중 오류가 발생했습니다. 다시 시도하세요.");
        return;
      }
      setRequesting(false);
    } else if (nextAction === "disable_bio") {
      setRequesting(true);
      try {
        setBioEnabled(false);
        const data = await updateBioMutation.mutateAsync(false);
        if (typeof data?.isBiometricEnabled === "boolean") {
          setBioEnabled(!!data.isBiometricEnabled);
        }
      } catch (e) {
        // 실패 시 재시도 가능하도록 상태 원복
        didCompleteRef.current = false;
        setRequesting(false);
        setHelpText("처리 중 오류가 발생했습니다. 다시 시도하세요.");
        return;
      }
      setRequesting(false);
    }
    try {
      goBack();
    } catch {}
  }, [
    nextAction,
    disableMutation,
    setEnabled,
    setPinHash,
    setBioEnabled,
    updateBioMutation,
    goBack,
  ]);

  // Keep a stable ref to always call the latest onAuthSuccess without retriggering biometric effect
  const onAuthSuccessRef = useRef(onAuthSuccess);
  // Guard to ensure biometric auto-request runs only once per screen entry
  const hasTriggeredBioRef = useRef(false);
  useEffect(() => {
    onAuthSuccessRef.current = onAuthSuccess;
  }, [onAuthSuccess]);

  // 생체 인증 지원 여부 체크
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const info = await nativeBridge.getBiometricStatus(3500);
        if (!mounted) return;
        setBioSupported(!!info?.available);
      } catch {
        if (!mounted) return;
        setBioSupported(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // 생체 인증 자동 실행 (설정 ON + 기기 지원)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!bioEnabled || !bioSupported || hasTriggeredBioRef.current) return;
      hasTriggeredBioRef.current = true;
      try {
        if (!cancelled) setIsBioRequesting(true);
        const res = await nativeBridge.requestBiometric({
          promptMessage: "잠금 해제를 위한 생체 인증",
          cancelLabel: "취소",
          disableDeviceFallback: false,
        });
        if (!cancelled && res?.success) {
          onAuthSuccessRef.current?.();
        }
      } catch {
        // 실패/취소 시 PIN 입력으로 계속 진행
      } finally {
        if (!cancelled) setIsBioRequesting(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [bioEnabled, bioSupported]);

  const onInput = React.useCallback(
    (digit: string) => {
      if (requesting) return;
      setError("");
      if (!/^\d$/.test(digit)) return;
      setPin((prev) => (prev + digit).slice(0, 4));
    },
    [requesting]
  );

  const onBackspace = React.useCallback(() => {
    if (requesting) return;
    setError("");
    setPin((prev) => prev.slice(0, -1));
  }, [requesting]);

  const onClear = React.useCallback(() => {
    if (requesting) return;
    setError("");
    setPin("");
  }, [requesting]);

  // PIN 4자리 입력 시 서버 API로 검증 요청
  useEffect(() => {
    const verify = async () => {
      if (pin.length !== 4) return;
      if (!isValidPin(pin)) {
        setError("비밀번호는 숫자 4자리여야 합니다.");
        setPin("");
        return;
      }
      setRequesting(true);
      try {
        const res = await appLockApi.verifyPin(pin);
        if (res?.isSuccess) {
          onAuthSuccess();
        } else {
          setHelpText("비밀번호가 일치하지 않아요.");
          setPin("");
        }
      } catch (e) {
        setError("확인 중 오류가 발생했습니다. 다시 시도하세요.");
        setPin("");
      } finally {
        setRequesting(false);
      }
    };
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  return (
    <>
      <Box
        ref={headerRef}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <TaskHeader
          key={`pin_setup_${userState?.userId}`}
          taskKey={`pin_setup-${userState?.userId}`}
          title={"비밀번호 입력"}
        />
      </Box>
      <ContentTemplateV2>
        <Stack
          spacing={4}
          alignItems="center"
          justifyContent="center"
          sx={{ minHeight: availableHeight ? `${availableHeight}px` : "60vh" }}
        >
          <Stack spacing={1} alignItems="center">
            <Typography
              variant={"Body"}
              sx={{
                fontWeight: 500,
                color: theme.vars.palette.Content.Medium,
              }}
            >
              {helpText}
            </Typography>
          </Stack>

          <Stack spacing={3} alignItems="center" sx={{ mt: 2 }}>
            <Dots count={pin.length} />
          </Stack>
        </Stack>
      </ContentTemplateV2>

      <FloatingArea
        noDivider={true}
        fullWidth={!!isInappWebview}
        ref={floatingRef}
      >
        <Box sx={{ mb: 3 }}>
          <CustomTypography
            variant={"Caption"}
            sx={{
              fontWeight: 500,
              color: (theme) => theme.vars.palette.Content.Medium,
            }}
          >
            {"비밀번호를 잊으셨나요? "}
            <Box
              component="a"
              onClick={(e) => {
                e.preventDefault();
                openChat();
              }}
              sx={{
                textDecoration: "underline",
                fontWeight: 700,
                color: (theme) => theme.vars.palette.Content.Highlight,
                cursor: "pointer",
              }}
            >
              문의하기
            </Box>
          </CustomTypography>
        </Box>
        <NumericKeypad
          onInput={onInput}
          onBackspace={onBackspace}
          onClear={onClear}
        />
      </FloatingArea>
      {isBioRequesting && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            zIndex: 9999,
          }}
        />
      )}
    </>
  );
}
